import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from './Modal';

// #1 — single source of truth for primary color
const PRIMARY = '#2c3e50';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// #13 — only show meaningful milestones, filter frame noise
function toMilestone(msg, progress) {
  if (progress <= 15)                          return 'Uploading video...';
  if (msg.toLowerCase().includes('calibrat')) return 'Calibrating thresholds...';
  if (msg.toLowerCase().includes('rep '))     return msg; // "Rep 3 complete — 9.1/10"
  if (msg.toLowerCase().includes('complete')) return 'Analysis complete!';
  if (msg.toLowerCase().includes('frame'))    return null; // suppress frame heartbeat
  if (msg.toLowerCase().includes('angle'))    return null; // suppress angle updates
  return msg;
}

export default function VideoUploadScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { exerciseName } = location.state || { exerciseName: 'Exercise' };

  const [videos, setVideos] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [lastAnalyzedVideo, setLastAnalyzedVideo] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('uploadedVideos') || '[]');
      setVideos(saved.map(v => ({ ...v, videoFile: null, previewUrl: null, needsReselect: !v.result })));
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (videos.length === 0) return;
    const toSave = videos.map(({ videoFile, previewUrl, needsReselect, ...rest }) => rest);
    localStorage.setItem('uploadedVideos', JSON.stringify(toSave));
  }, [videos]);

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files);
    setVideos(prev => [...prev, ...files.map(file => ({
      id: Date.now() + Math.random(),
      videoName: file.name,
      videoFile: file,
      previewUrl: URL.createObjectURL(file),
      result: null,
      exerciseName,
      uploadDate: new Date().toISOString(),
      needsReselect: false,
    }))]);
    e.target.value = '';
  };

  const handleReselect = (videoId) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'video/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setVideos(prev => prev.map(v => v.id === videoId
        ? { ...v, videoFile: file, videoName: file.name, previewUrl: URL.createObjectURL(file), needsReselect: false }
        : v));
    };
    input.click();
  };

  const handleAnalyze = async (video) => {
    if (!video.videoFile) return;
    setUploadingId(video.id);
    setProgress(10);
    setStatusMessage('Uploading video...');

    const formData = new FormData();
    formData.append('video', video.videoFile);
    formData.append('exerciseName', video.exerciseName);

    try {
      const response = await fetch(`${API_URL}/api/analyze`, { method: 'POST', body: formData });
      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.error || 'Analysis failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'status') {
              const newProg = Math.min(progress + 2, 88);
              setProgress(newProg);
              const milestone = toMilestone(event.message, newProg);
              if (milestone) setStatusMessage(milestone);
            } else if (event.type === 'rep') {
              setStatusMessage(`Rep ${event.rep} complete — ${event.rating}/10`);
              setProgress(prev => Math.min(prev + 5, 92));
            } else if (event.type === 'frame') {
              // #13 — log to console only, don't update UI
              console.log(`Frame: angle=${event.angle} reps=${event.reps}`);
            } else if (event.type === 'done') {
              setProgress(100);
              setStatusMessage('Analysis complete!');
              const updatedVideo = { ...video, result: event.result };
              setVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
              setLastAnalyzedVideo(updatedVideo);
              setUploadingId(null);
              setShowModal(true);
              return;
            } else if (event.type === 'error') {
              throw new Error(event.message);
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      alert(`Analysis failed: ${err.message === 'Failed to fetch' ? 'Backend not running on port 5001.' : err.message}`);
      setUploadingId(null);
      setProgress(0);
      setStatusMessage('');
    }
  };

  const completedCount = videos.filter(v => v.result).length;
  const isUploading = uploadingId !== null;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={() => navigate('/')} style={iconBtn}>←</button>
        <div style={{ flex: 1 }}>
          {/* #3 — consistent 12px caption, 18px heading */}
          <p style={{ fontSize: '12px', color: '#6c757d', margin: 0 }}>Step 2 of 3</p>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: PRIMARY, margin: 0 }}>Upload Video</h2>
        </div>
        {videos.length > 0 && (
          <button onClick={() => setShowClearModal(true)} style={{ padding: '6px 14px', background: '#ffffff', border: '1px solid #e9ecef', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: '#6c757d' }}>
            Clear
          </button>
        )}
      </div>

      <div style={{ padding: '16px 20px' }}>
        {/* Exercise badge */}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '14px 18px', marginBottom: '14px', border: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: '12px', color: '#6c757d', margin: '0 0 2px' }}>Selected Exercise</p>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: PRIMARY, margin: 0 }}>{exerciseName}</h3>
          </div>
          <span style={{ fontSize: '12px', color: '#6c757d', backgroundColor: '#f8f9fa', padding: '6px 10px', borderRadius: '8px' }}>Side view</span>
        </div>

        {/* #10 — compact upload area */}
        <input ref={fileInputRef} type="file" accept="video/*" multiple onChange={handleVideoSelect} style={{ display: 'none' }} />
        <div
          onClick={() => !isUploading && fileInputRef.current.click()}
          style={{ border: `2px dashed ${isUploading ? '#dee2e6' : '#ced4da'}`, borderRadius: '12px', padding: '22px 20px', textAlign: 'center', backgroundColor: '#ffffff', cursor: isUploading ? 'not-allowed' : 'pointer', marginBottom: '16px', opacity: isUploading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}
        >
          <svg style={{ width: '28px', height: '28px', color: '#adb5bd', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.87V15.13a1 1 0 01-1.447.9L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
          </svg>
          <div style={{ textAlign: 'left' }}>
            <p style={{ fontSize: '14px', fontWeight: '500', color: PRIMARY, margin: 0 }}>Tap to select video</p>
            <p style={{ fontSize: '12px', color: '#6c757d', margin: 0 }}>MP4, MOV or AVI · Max 150MB</p>
          </div>
        </div>

        {/* Video list */}
        {videos.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: PRIMARY, margin: 0 }}>Videos ({videos.length})</h4>
              {/* #14 — only show "saved" when at least one has a result */}
              {completedCount > 0 && <span style={{ fontSize: '12px', color: '#10b981' }}>{completedCount} analyzed · saved</span>}
            </div>

            {videos.map((video, idx) => (
              // #12 — amber border when needs re-select
              <div key={video.id} style={{ backgroundColor: '#ffffff', borderRadius: '12px', marginBottom: '10px', border: `1px solid ${video.needsReselect ? '#ffc107' : '#e9ecef'}`, overflow: 'hidden' }}>
                {/* #11 — small inline thumbnail instead of full-width */}
                <div style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {video.previewUrl ? (
                    <video src={video.previewUrl} style={{ width: '64px', height: '48px', objectFit: 'cover', borderRadius: '6px', backgroundColor: '#000', flexShrink: 0 }} muted playsInline />
                  ) : (
                    <div style={{ width: '64px', height: '48px', backgroundColor: '#f8f9fa', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#adb5bd' }}>{idx + 1}</span>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '13px', fontWeight: '500', color: PRIMARY, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{video.videoName}</p>
                    <p style={{ fontSize: '12px', color: '#6c757d', margin: 0 }}>{video.exerciseName}</p>
                  </div>
                  {video.result ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: parseFloat(video.result.average_rating) >= 8 ? '#10b981' : '#d97706' }}>
                        {parseFloat(video.result.average_rating).toFixed(1)}/10
                      </span>
                      <button onClick={() => navigate('/result', { state: { video } })}
                        style={{ padding: '6px 12px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', color: PRIMARY, fontWeight: '500' }}>
                        View
                      </button>
                    </div>
                  ) : video.needsReselect ? (
                    <button onClick={() => handleReselect(video.id)}
                      style={{ padding: '6px 12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', color: '#856404', fontWeight: '500', flexShrink: 0 }}>
                      Re-select
                    </button>
                  ) : uploadingId === video.id ? (
                    <span style={{ fontSize: '12px', color: '#6c757d', flexShrink: 0 }}>Analyzing...</span>
                  ) : (
                    <button onClick={() => handleAnalyze(video)} disabled={isUploading}
                      style={{ padding: '7px 14px', backgroundColor: isUploading ? '#e9ecef' : PRIMARY, color: isUploading ? '#adb5bd' : '#ffffff', border: 'none', borderRadius: '8px', cursor: isUploading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500', flexShrink: 0 }}>
                      Analyze
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Processing card */}
        {isUploading && (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '20px', textAlign: 'center', border: '1px solid #e9ecef', marginTop: '8px' }}>
            <p style={{ fontSize: '14px', fontWeight: '500', color: PRIMARY, marginBottom: '12px' }}>{statusMessage}</p>
            <div style={{ width: '100%', height: '6px', backgroundColor: '#e9ecef', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' }}>
              <div style={{ height: '100%', backgroundColor: PRIMARY, borderRadius: '3px', width: `${progress}%`, transition: 'width 0.4s ease' }} />
            </div>
            <p style={{ fontSize: '12px', color: '#adb5bd', margin: 0 }}>This may take a minute...</p>
          </div>
        )}

        {completedCount > 0 && completedCount === videos.length && !isUploading && (
          <button onClick={() => navigate('/overview', { state: { videos } })}
            style={{ width: '100%', padding: '14px', backgroundColor: PRIMARY, color: '#ffffff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', marginTop: '8px' }}>
            View All Results ({completedCount}) →
          </button>
        )}
      </div>

      {/* Analysis Complete Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
        <div style={{ textAlign: 'center', padding: '28px 24px' }}>
          <div style={{ width: '52px', height: '52px', backgroundColor: '#10b981', borderRadius: '50%', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="24" height="24" fill="none" stroke="white" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: PRIMARY, marginBottom: '8px' }}>Analysis Complete</h3>
          <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '24px' }}>Your video has been analyzed successfully.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button onClick={() => { setShowModal(false); lastAnalyzedVideo && navigate('/result', { state: { video: lastAnalyzedVideo } }); }}
              style={{ width: '100%', padding: '13px', backgroundColor: PRIMARY, color: '#ffffff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
              View Results
            </button>
            <button onClick={() => { setShowModal(false); fileInputRef.current.click(); }}
              style={{ width: '100%', padding: '13px', backgroundColor: '#f8f9fa', color: PRIMARY, border: '1px solid #e9ecef', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
              Upload Another
            </button>
            <button onClick={() => { setShowModal(false); navigate('/'); }}
              style={{ width: '100%', padding: '13px', backgroundColor: 'transparent', color: '#6c757d', border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer' }}>
              New Exercise
            </button>
          </div>
        </div>
      </Modal>

      {/* Clear Modal */}
      <Modal isOpen={showClearModal} onClose={() => setShowClearModal(false)}>
        <div style={{ textAlign: 'center', padding: '28px 24px' }}>
          <h3 style={{ fontSize: '17px', fontWeight: '600', color: PRIMARY, marginBottom: '8px' }}>Clear all videos?</h3>
          <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '24px' }}>
            This will remove {videos.length} video{videos.length !== 1 ? 's' : ''} from your list.
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setShowClearModal(false)} style={{ flex: 1, padding: '12px', backgroundColor: '#f8f9fa', color: PRIMARY, border: '1px solid #e9ecef', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => { localStorage.removeItem('uploadedVideos'); setVideos([]); setShowClearModal(false); }}
              style={{ flex: 1, padding: '12px', backgroundColor: PRIMARY, color: '#ffffff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Clear</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const iconBtn = { width: '40px', height: '40px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '12px', fontSize: '20px', cursor: 'pointer', color: '#2c3e50', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
